import React, { useState, useEffect } from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Button, Divider, Typography, message } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, SoundFilled } from '@ant-design/icons';
import useAudioCache from '../../../hooks/useAudioCache';
import useTranslation from '../../../hooks/useTranslation';
import { shuffled } from '../../../utils/random';

const { Text } = Typography;


/** Нажатая плитка розовеет при ошибке; верная и все прочие остаются белыми. */
const tileBackground = (buttonState, index) =>
    buttonState.index === index && !buttonState.isCorrect ? '#ffdada' : 'white';

function SpellingFromAudioEnglishGbWordCard({
                                                word,
                                                handleCorrectClick,
                                                handleIncorrectClick,
                                                locale,
                                            }) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { playAudio } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);
    const [openExamples, setOpenExamples] = useState({});
    const [selectedLetters, setSelectedLetters] = useState([]);
    const [shuffledLetters, setShuffledLetters] = useState([]);
    const [errors, setErrors] = useState(0);
    const [buttonState, setButtonState] = useState({ index: null, isCorrect: null });
    const [isWordComplete, setIsWordComplete] = useState(false);
    const [isCorrectButtonDisabled, setIsCorrectButtonDisabled] = useState(false);



    const shuffleLetters = (str) =>
        // Объекты с id, буквой и флагом chosen
        shuffled(str.split('')).map((letter, index) => ({ id: index, letter, chosen: false }));

    useEffect(() => {
        setShuffledLetters(shuffleLetters(word.headword));
        setOpenExamples({});
        setSelectedLetters([]);
        setIsWordComplete(false);
        setIsCorrectButtonDisabled(false);
        setErrors(0);
    }, [word]);

    const toggleExamples = (index) => {
        setOpenExamples((prevState) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
    };

    // Озвучка слова проигрывается при показе карточки — поведение практики
    // сохранено, теперь по ref из ответа и через общий кэш useAudioCache.
    useEffect(() => {
        if (word.audio?.access === 'ALLOWED') {
            playAudio(word.audio.ref);
        }
    }, [word, playAudio]);



    // Замок в данных: PLAN_REQUIRED объясняется словами, а не тишиной.
    const playExampleAudio = (example) => {
        if (example.audio?.access === 'ALLOWED' || example.audio?.access === 'NOT_READY') {
            playAudio(example.audio.ref, example.audio.access);
        } else if (example.audio?.access === 'PLAN_REQUIRED') {
            message.info(t('common.exampleAudioPremium'));
        }
    };

    const handleNextClick = () => {
        if (errors === 0) {
            // Карточка считает ошибки сама: это и есть число попыток минус одна.
            handleCorrectClick({ attempts: errors + 1 });
        } else {
            handleIncorrectClick({ attempts: errors + 1 });
        }
    };


    const handleLetterClick = (letter, index) => {
        setIsCorrectButtonDisabled(true); // Делаем кнопку Correct неактивной при первом клике
        const formedWord = [...selectedLetters.map((chosen) => chosen.letter), letter].join('');
        const isCorrect = word.headword.startsWith(formedWord);
        setButtonState({ index, isCorrect });
        if (isCorrect) {
            const newSelectedLetters = [...selectedLetters, { id: shuffledLetters[index].id, letter }];
            setSelectedLetters(newSelectedLetters);
            // Отмечаем выбранную букву как chosen
            setShuffledLetters((prevShuffledLetters) =>
                prevShuffledLetters.map((item, i) =>
                    i === index ? { ...item, chosen: true } : item
                )
            );
            // Проверяем, завершено ли слово
            if (newSelectedLetters.length === word.headword.length) {
                setIsWordComplete(true);
            }
        } else {
            setErrors((prevErrors) => prevErrors + 1);
        }

        setTimeout(() => setButtonState({ index: null, isCorrect: null }), 500);
    };



    return (
        <div
            style={{
                padding: isMobile ? '0' : '0px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 'calc(100vh - 80px)',
                overflow: 'hidden',
                width: isMobile ? '100%' : '33%',
                margin: isMobile ? '0' : 'auto',
            }}
        >
            <Card style={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '0px'
            }}>
                <CardContent style={{flexGrow: 1, padding: isMobile ? '0px' : '20px'}}>
                    <div>
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '50px',
                            }}
                        >
                            {selectedLetters.map((chosen) => (
                                <Text key={chosen.id} strong
                                      style={{fontSize: '2em', color: '#027b83', textAlign: 'center'}}>
                                    {chosen.letter}
                                </Text>
                            ))}
                        </div>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>

                            {isWordComplete && (
                            <button
                                type="button"
                                aria-label={t('common.listen')}
                                style={{
                                    borderRadius: '25%',
                                    padding: '3px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: '1.5px solid #838383',
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playAudio(word.audio?.ref, word.audio?.access);
                                }}
                            >
                                <SoundFilled style={{fontSize: '0.9em', color: '#838383'}}/>
                            </button>
                            )}

                            {isWordComplete && (
                                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <Text style={{fontSize: '1em', marginLeft: '8px'}}>/{word.phonetic}/</Text>

                                </div>
                            )}


                        </div>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>

                            <Text strong style={{
                                fontSize: '1.2em',
                                marginTop: '8px',
                                textAlign: 'center',
                                color: isWordComplete ? '#000' : 'rgba(0, 0, 0, 0)',                            }}>
                                {word.partOfSpeech}
                            </Text>


                        </div>
                    </div>

                    <Divider style={{margin: isMobile ? '0px 0' : '10px 0'}}/>


                    {isWordComplete ? (
                            <div style={{marginTop: '10px'}}>
                                {word.wordDefinition.map((def, index) => (
                                    <div key={def.id} style={{marginBottom: '16px'}}>
                                        <button
                                            type="button"
                                            aria-expanded={Boolean(openExamples[index])}
                                            onClick={() => toggleExamples(index)}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center', background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', width: '100%',
                                            }}
                                        >
                                            {openExamples[index] ? (
                                                <CaretUpOutlined style={{marginLeft: '8px', color: '#d9d9d9'}}/>
                                            ) : (
                                                <CaretDownOutlined style={{marginLeft: '8px', color: '#d9d9d9'}}/>
                                            )}
                                            <Text strong style={{marginLeft: '8px'}}>{def.definition}</Text>
                                        </button>
                                        <div style={{marginTop: '4px'}}>
                                            <Text italic strong style={{marginLeft: '36px', color: '#ee6002'}}>
                                                {def.translation}
                                            </Text>
                                        </div>

                                        {openExamples[index] && (
                                            <div style={{marginTop: '10px', marginLeft: '16px'}}>
                                                {def.examples.map((example, exampleIndex) => (
                                                    <div key={example.id} style={{marginBottom: '8px'}}>
                                                        <button
                                                            type="button"
                                                            aria-label={t('common.listen')}
                                                            style={{
                                                                border: '1px solid #027b83',
                                                                borderRadius: '25%',
                                                                padding: '3px',
                                                                display: 'inline-flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                                background: 'none',
                                                                marginRight: '8px',
                                                            }}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                playExampleAudio(example);
                                                            }}
                                                        >
                                                            <SoundFilled style={{fontSize: '0.6em', color: '#027b83'}}/>
                                                        </button>
                                                        <Text>{example.sentence}</Text>
                                                        <br/>
                                                        <Text type="secondary">{example.translation}</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{


                                justifyContent: 'center', // Центрирование по горизонтали
                                alignItems: 'center', // Центрирование по вертикали внутри контейнера
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',

                                marginTop: '20%'
                             //    marginTop: '20%', // Отступ сверху на 60% высоты экрана
                                // transform: 'translateY(-50%)', // Центрирование блока на середине экрана с учётом отступа



                            }}>
                                {shuffledLetters.map(({id, letter, chosen}, index) => (
                                    <Button
                                        key={id}
                                        onClick={() => !chosen && handleLetterClick(letter, index)}
                                        type="text"
                                        disabled={chosen}
                                        style={{
                                            fontSize: '1.5em',
                                            fontWeight: 'bold',
                                            width: '38px',
                                            height: '38px',
                                            color: '#027b83',
                                            borderColor: '#027b83',
                                            backgroundColor: tileBackground(buttonState, index),
                                            transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                            pointerEvents: chosen ? 'none' : 'auto',
                                            visibility: chosen ? 'hidden' : 'visible',
                                        }}
                                    >
                                        {letter}
                                    </Button>
                                ))}
                            </div>
                        )}


                </CardContent>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '6px',
                        paddingLeft: '20px',
                        paddingBottom: '20px',
                        paddingRight: '20px',
                    }}
                >
                    <Button
                        onClick={handleCorrectClick}
                        disabled={isCorrectButtonDisabled} // Кнопка неактивна, если хотя бы одна буква выбрана
                        style={{
                            width: isMobile ? '100%' : '150px',
                            marginRight: '10px',
                            cursor: isCorrectButtonDisabled ? 'default' : 'pointer', // Курсор меняется в зависимости от состояния
                        }}
                    >
                        Know
                    </Button>

                    <Button
                        onClick={handleNextClick}
                        disabled={!isWordComplete} // Отключена, если слово не завершено
                        style={{
                            width: isMobile ? '100%' : '150px',
                            cursor: isWordComplete ? 'pointer' : 'default', // Меняем курсор
                        }}
                    >
                        Next
                    </Button>


                </div>
            </Card>
        </div>
    );
}

export default SpellingFromAudioEnglishGbWordCard;
